import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandMark from "@/components/base/BrandMark";
import SiteButton from "@/components/site/SiteButton";

interface LandingHeaderProps {
  onRequest: () => void;
}

const navLinks = [
  { href: "#plateforme", label: "Plateforme" },
  { href: "#fonctionnalites", label: "Modules" },
  { href: "#hors-ligne", label: "Hors ligne" },
  { href: "#etapes", label: "Démarrer", wide: true },
  { href: "#tarifs", label: "Tarifs" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact", wide: true },
];

/** En-tête flottant arrondi : il se détache du haut de page dès qu'on défile. */
export default function LandingHeader({ onRequest }: LandingHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6 md:pt-4">
      <div
        className={`mx-auto max-w-6xl rounded-[1.75rem] border bg-white/95 backdrop-blur transition-shadow duration-300 ${
          scrolled || menuOpen ? "border-background-200 shadow-raised" : "border-background-200/70 shadow-card"
        }`}
      >
        <div className="flex h-16 items-center justify-between gap-4 pl-3 pr-2 md:h-[4.5rem] md:pl-5 md:pr-3">
          <Link to="/" className="flex items-center gap-2.5" aria-label="WaitechEdu, accueil">
            <BrandMark />
            <span className="text-lg font-bold font-heading tracking-tight text-primary-950">
              Waitech<span className="text-primary-500">Edu</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Sections de la page">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                // Écran moyen : les liens « larges » passent dans le menu pour que tout tienne sur une ligne.
                className={`${l.wide ? "hidden 2xl:inline-flex" : "inline-flex"} whitespace-nowrap rounded-full px-3 py-2 text-[0.95rem] font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950 xl:px-3.5`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <Link
              to="/login"
              className="whitespace-nowrap rounded-full px-4 py-2 text-[0.95rem] font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              Se connecter
            </Link>
            <SiteButton onClick={onRequest}>Demander un compte</SiteButton>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="press flex h-11 w-11 items-center justify-center rounded-full text-foreground-800 hover:bg-background-100 lg:hidden"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 px-3 pb-4 pt-2 lg:hidden">
            <nav className="flex flex-col" aria-label="Sections de la page">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-xl px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/suivre-demande"
                onClick={() => setMenuOpen(false)}
                className="rounded-xl px-3 py-3 text-base font-medium text-foreground-800 hover:bg-background-100"
              >
                Suivre ma demande
              </Link>
            </nav>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <SiteButton to="/login" variant="outline">
                Se connecter
              </SiteButton>
              <SiteButton
                onClick={() => {
                  setMenuOpen(false);
                  onRequest();
                }}
              >
                Demander un compte
              </SiteButton>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
